import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  const fullPath = normalizedPath.startsWith('uploads/')
    ? normalizedPath
    : `uploads/${normalizedPath}`;
  return `${backendBase}/${fullPath}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const UpdateDoctor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState({
    department_id: '',
    type: 'opd',
    name: '',
    education: '',
    number: '',
    image: null,
    status: 'active',
  });

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await getRequest('departments?limit=500');
        if (res?.data?.success) {
          setDepartments(res.data.data || []);
        }
      } catch {
        // silent
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    const fetchDoctor = async () => {
      setFetching(true);
      try {
        const res = await getRequest(`doctors/${id}`);
        if (res?.data?.success) {
          const item = res.data.data;
          setCurrent(item);
          setFormData({
            department_id: item.department_id || '',
            type: item.type || 'opd',
            name: item.name || '',
            education: item.education || '',
            number: item.number || '',
            image: null,
            status: item.status || 'active',
          });
        } else {
          toast.error(res?.data?.message || 'Failed to load doctor');
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error fetching doctor');
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchDoctor();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (e.target.type === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.department_id) {
      toast.error('Department is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Doctor name is required');
      return;
    }

    setLoading(true);

    const payload = new FormData();
    payload.append('department_id', formData.department_id);
    payload.append('type', formData.type);
    payload.append('name', formData.name.trim());
    payload.append('education', formData.education.trim());
    payload.append('number', formData.number.trim());
    payload.append('status', formData.status);

    if (formData.image) {
      payload.append('image', formData.image);
    }

    try {
      const response = await patchRequest(`doctors/${id}`, payload, getAuthHeaders());

      if (response?.data?.success) {
        toast.success('Doctor updated successfully');
        navigate('/doctors');
      } else {
        toast.error(response?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating doctor');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="text-center mt-5">Loading doctor...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Update Doctor</h4>
      </div>

      <form onSubmit={handleSubmit} className="form-style">
        <div className="row mx-4 mt-5">
          <div className="col-md-6 mt-2">
            <label htmlFor="department_id">Department *</label>
            <select
              id="department_id"
              name="department_id"
              className="form-control"
              value={formData.department_id}
              onChange={handleChange}
              required
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.heading}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              className="form-control"
              value={formData.type}
              onChange={handleChange}
            >
              <option value="opd">OPD</option>
              <option value="ipd">IPD</option>
            </select>
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="name">Doctor Name *</label>
            <input
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="education">Education (optional)</label>
            <input
              id="education"
              name="education"
              className="form-control"
              value={formData.education}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="number">Number (optional)</label>
            <input
              id="number"
              name="number"
              className="form-control"
              value={formData.number}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="image">Image (Leave blank to keep current)</label>
            <input
              id="image"
              name="image"
              type="file"
              className="form-control-file"
              accept=".png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.avif,.tiff,.ico,.jfif,.pjpeg,.pjp,image/png,image/jpeg,image/jpg,image/webp,image/gif,image/bmp,image/svg+xml,image/avif,image/tiff,image/x-icon,image/jfif,image/*"
              onChange={handleChange}
            />
            {formData.image && (
              <small className="text-muted d-block mt-1">Selected: {formData.image.name}</small>
            )}
            {!formData.image && current?.image && (
              <div className="mt-2">
                <img
                  src={getImageUrl(current.image)}
                  alt={current.name}
                  width={120}
                  style={{ borderRadius: '4px', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>

          <div className="col-md-6 mt-2">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-lg addbtn" disabled={loading}>
            {loading ? 'Updating...' : 'Update Doctor'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateDoctor;
